import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import {CAN_DOWNLOAD, saveBlob, saveResponseAsFile} from '@/shared/lib/download';
import {AppButton} from '@/shared/ui/button';
import {Banner} from '@/shared/ui/banner';
import {HelpNote} from '@/shared/ui/explain';
import {AppText} from '@/shared/ui/text';
import {spacing, useTheme} from '@/shared/ui/theme';

/**
 * The two ways a report leaves the app.
 *
 * `onServer` is the gateway's own renderer — a real .xlsx with the server's formatting,
 * available for a department and for a person. `onLocal` is the CSV this app builds
 * from the rows on screen, which is the ONLY option at company width (there is no
 * company-wide export route) and the better one whenever a filter is applied, because
 * it exports what the user is actually looking at.
 *
 * ## Saying what comes out
 *
 * Three buttons labelled "Скачать XLSX", "Скачать CSV" and "CSV по выборке" is a quiz:
 * two of them differ only in a file extension and the third in a word nobody outside
 * this codebase reads as "the filtered rows". So the bar now states, above the buttons,
 * the FILE NAME it is about to produce and WHAT IS IN IT — the period, the scope, and
 * how many rows. A download that lands in the browser's Downloads folder and cannot be
 * traced back to the screen that made it is a support ticket a week later.
 *
 * On native there is nowhere to put a file (shared/lib/download says so honestly rather
 * than offering a button that throws), and the bar says THAT too instead of rendering
 * nothing: an export that is simply absent on the phone reads as a missing feature, and
 * somebody goes looking for it.
 *
 * It draws no card of its own: it lives inside the sheet the toolbar's ↓ pill opens,
 * and a card on an elevated surface is a seam rather than a panel.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(3)},
  head: {gap: 2},
  row: {flexDirection: 'row', gap: spacing(2), flexWrap: 'wrap'},
  button: {flexGrow: 1, flexBasis: 150},
});

export function ExportBar({
  onServer,
  onLocal,
  fileName,
  /** One line naming what the file will contain — period, scope, row count. */
  contents,
  /**
   * How the two files differ, when the default sentence is wrong.
   *
   * The default says the server file is «весь период целиком», which is true of the
   * attendance exports this bar was built for — those routes take the period. The log
   * export does NOT: `GET /control/logs/export` accepts every filter except a date, so
   * there the server file is the whole history and the local one is the period. Same
   * two buttons, opposite meaning, and stating the attendance version over the log
   * would be worse than saying nothing.
   */
  note,
}: {
  /** Fetches the server-rendered file. Omitted at company width. */
  onServer?: (format: 'xlsx' | 'csv') => Promise<Response>;
  /** Builds the CSV locally from what is on screen. */
  onLocal?: () => Blob;
  /** Without extension — each handler appends its own. */
  fileName: string;
  contents?: string;
  note?: string;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const [busy, setBusy] = useState<'xlsx' | 'csv' | 'local' | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!CAN_DOWNLOAD) {
    return <HelpNote text={t('reports.exportWebOnly')} />;
  }

  const runServer = async (format: 'xlsx' | 'csv') => {
    if (!onServer) {
      return;
    }
    setBusy(format);
    setError(null);
    try {
      const res = await onServer(format);
      await saveResponseAsFile(res, `${fileName}.${format}`);
    } catch {
      setError(t('reports.exportFailed'));
    } finally {
      setBusy(null);
    }
  };

  const runLocal = () => {
    if (!onLocal) {
      return;
    }
    setBusy('local');
    setError(null);
    try {
      saveBlob(onLocal(), `${fileName}.csv`);
    } catch {
      setError(t('reports.exportFailed'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <View style={styles.root} testID="export-bar">
      <View style={styles.head}>
        {contents ? <AppText variant="body">{contents}</AppText> : null}
        <AppText variant="mono" color={p.mutedForeground} numberOfLines={1}>
          {fileName}
        </AppText>
      </View>

      <View style={styles.row}>
        {onServer ? (
          <>
            <AppButton
              title={t('reports.exportXlsx')}
              tone="soft"
              size="sm"
              loading={busy === 'xlsx'}
              disabled={busy !== null}
              onPress={() => void runServer('xlsx')}
              style={styles.button}
              testID="export-xlsx"
            />
            <AppButton
              title={t('reports.exportCsv')}
              tone="neutral"
              size="sm"
              loading={busy === 'csv'}
              disabled={busy !== null}
              onPress={() => void runServer('csv')}
              style={styles.button}
              testID="export-csv"
            />
          </>
        ) : null}
        {onLocal ? (
          <AppButton
            title={t(onServer ? 'reports.exportVisible' : 'reports.exportCsv')}
            tone={onServer ? 'ghost' : 'soft'}
            size="sm"
            loading={busy === 'local'}
            disabled={busy !== null}
            onPress={runLocal}
            style={styles.button}
            testID="export-local"
          />
        ) : null}
      </View>

      {/* Only worth saying where there are two sources: the server file is the whole
          scope, the local one is the filtered view. That difference is the entire
          reason both buttons exist. */}
      {onServer && onLocal ? (
        <HelpNote text={note ?? t('reports.exportDiffers')} />
      ) : null}

      {error ? <Banner tone="error" title={error} /> : null}
    </View>
  );
}
